/**
 * Pennsylvania State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual 28 Pa. Code Chapter 601
 * 
 * Regulatory Body: Pennsylvania Department of Health (PA DOH)
 * Primary Regulation: 28 Pa. Code Chapter 601 - Home Health Care Agencies
 * Enabling Statute: 35 P.S. §§ 448.101-448.904b (Health Care Facilities Act)
 * 
 * CRITICAL CORRECTION: Original file cited Chapter 611 throughout
 * Chapter 611 = HOME CARE AGENCIES AND HOME CARE REGISTRIES (non-medical, personal care)
 * Chapter 601 = HOME HEALTH CARE AGENCIES (skilled nursing, therapy - correct regulation)
 * 
 * VERIFIED Chapter 601 Structure:
 * - § 601.1 - Legal base
 * - § 601.2 - Affected home health care agencies
 * - § 601.3 - Requirements for home health care agencies
 * - § 601.6 - Definitions
 * - § 601.11 - Licensure process
 * - § 601.12 - Issuance of license
 * - § 601.13 - Responsibility of home health care agency owners
 * - § 601.14 - Departmental refusals
 * - § 601.21 - Organization, services and administration
 * - § 601.22 - Agency evaluation and review
 * - § 601.31 - Acceptance of patients, plan of treatment and medical supervision
 * - § 601.32 - Skilled nursing service
 * - § 601.33 - Therapy services
 * - § 601.34 - Medical and social services
 * - § 601.35 - Home health aide services
 * - § 601.36 - Clinical records
 * 
 * NOTE: Medicare-certified agencies are deemed compliant with this subpart
 * upon compliance with § 601.11(a)-(c) per § 601.2(c)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const PENNSYLVANIA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'PA-A1',
    itemId: 'A1',
    stateCode: 'PA',
    title: 'Home Health Care Agency License Status',
    description: 'Pennsylvania requires all home health care agencies to be licensed under 28 Pa. Code Chapter 601',
    severity: 'high',
    regulationCode: '§ 601.11',
    regulationName: 'License',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Care Agency license from PA DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 28 Pa. Code § 601.11, application fee is $50.',
      },
      {
        id: 'q2',
        question: 'What is your home health care agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is your agency Medicare certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 601.2(c), Medicare-certified agencies are deemed compliant with this subpart',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your PA DOH Home Health Care Agency license is current per 28 Pa. Code § 601.11.',
      atRisk: 'Your license expires soon. Submit renewal application to PA DOH immediately.',
      nonCompliant: 'Operating without a valid PA DOH license violates 28 Pa. Code Chapter 601. Contact PA DOH at (717) 787-8015.',
      unknown: 'Verify your license status with PA DOH at (717) 787-8015.',
    },
    stateNotes: [
      'License application/renewal fee: $50 per § 601.11(b)',
      'Medicare-certified agencies deemed compliant per § 601.2(c)',
      'Agency must report yearly statistics to DOH per § 601.11(c)',
    ],
  },

  {
    id: 'PA-A2',
    itemId: 'A2',
    stateCode: 'PA',
    title: 'Governing Body Requirements',
    description: 'Agency must have governing body assuming full legal authority per § 601.21(c)',
    severity: 'high',
    regulationCode: '§ 601.21(c)',
    regulationName: 'Governing Body',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a governing body or designated persons assuming full legal authority per § 601.21(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which governing body responsibilities are documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'appoint_admin', label: 'Appoints qualified administrator' },
          { value: 'professional_advice', label: 'Arranges for professional advice (§ 601.22)' },
          { value: 'bylaws', label: 'Adopts and periodically reviews written bylaws' },
          { value: 'fiscal', label: 'Oversees management and fiscal affairs' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets § 601.21(c) requirements.',
      atRisk: 'Governing body responsibilities may be incomplete per § 601.21(c).',
      nonCompliant: 'No governing body in place. This violates 28 Pa. Code § 601.21(c).',
      unknown: 'Review governing body structure against § 601.21(c).',
    },
    stateNotes: [
      'Governing body must assume full legal authority per 28 Pa. Code § 601.21(c)',
      'Must appoint a qualified administrator per § 601.21(c)(1)',
      'Must arrange for professional advice per § 601.22',
      'Must adopt and periodically review written bylaws per § 601.21(c)(3)',
      'Oversees management and fiscal affairs of the agency',
    ],
  },

  {
    id: 'PA-A3',
    itemId: 'A3',
    stateCode: 'PA',
    title: 'Administrator Qualifications',
    description: 'Administrator must meet qualifications per § 601.21(d) and § 601.6',
    severity: 'high',
    regulationCode: '§ 601.21(d)',
    regulationName: 'Administrator',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet one of the following qualifications per § 601.6?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Registered nurse' },
          { value: 'experience', label: 'Training/experience in health service admin + 1 year supervisory experience in home health or related programs' },
          { value: 'none', label: 'Does not meet any qualification' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is a qualified person authorized in writing to act in the administrator\'s absence per § 601.21(d)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['physician', 'rn', 'experience'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets § 601.21(d) and § 601.6 qualification requirements.',
      atRisk: 'Administrator alternate not designated in writing per § 601.21(d).',
      nonCompliant: 'Administrator does not meet qualifications per § 601.6.',
      unknown: 'Verify administrator qualifications against § 601.6.',
    },
    stateNotes: [
      'Administrator may also be the supervising physician or RN (§ 601.21(d)/(e))',
      'Must organize and direct agency functions',
      'Must maintain liaison among governing body, professional personnel, and staff',
      'Must employ qualified personnel and ensure adequate staff education',
    ],
  },

  {
    id: 'PA-A4',
    itemId: 'A4',
    stateCode: 'PA',
    title: 'Services Provided Requirements',
    description: 'Agency must provide skilled nursing plus at least one other therapeutic service per § 601.21(b)',
    severity: 'high',
    regulationCode: '§ 601.21(b)',
    regulationName: 'Services',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide part-time or intermittent skilled nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required under § 601.21(b)',
      },
      {
        id: 'q2',
        question: 'Which additional therapeutic services do you provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'pt', label: 'Physical therapy' },
          { value: 'ot', label: 'Occupational therapy' },
          { value: 'speech', label: 'Speech pathology' },
          { value: 'social', label: 'Medical social services' },
          { value: 'hha', label: 'Home health aides' },
        ],
        required: true,
        helpText: 'Must provide at least one other therapeutic service per § 601.21(b)',
      },
      {
        id: 'q3',
        question: 'Is at least one qualifying service provided directly through agency employees (not contracted)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 601.21(b), at least one qualifying service must be provided directly',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 1 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Services meet § 601.21(b) requirements (skilled nursing + at least one other therapeutic service).',
      atRisk: 'Service configuration may not fully meet § 601.21(b) requirements.',
      nonCompliant: 'Services do not meet § 601.21(b) requirements. Must provide skilled nursing plus at least one other therapeutic service.',
      unknown: 'Review service offerings against § 601.21(b).',
    },
    stateNotes: [
      'Must provide part-time or intermittent skilled nursing services per 28 Pa. Code § 601.21(b)',
      'Must provide at least one other therapeutic service (PT, OT, Speech, Social Work, HHA)',
      'At least one qualifying service must be provided directly by agency employees',
      'Remaining services may be provided under contract per § 601.21(b)',
    ],
  },

  {
    id: 'PA-A5',
    itemId: 'A5',
    stateCode: 'PA',
    title: 'Supervising Physician or RN',
    description: 'Skilled nursing and therapeutic services must be under supervision per § 601.21(e)',
    severity: 'high',
    regulationCode: '§ 601.21(e)',
    regulationName: 'Supervision',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Are skilled nursing and therapeutic services under supervision of a physician or RN per § 601.21(e)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the supervising physician or RN available at all times during operating hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no supervision OR supervisor not available during operating hours
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT: supervision in place AND available during all operating hours
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets § 601.21(e) requirements.',
      atRisk: 'Supervision may need improvement.',
      nonCompliant: 'No supervising physician or RN in place OR not available during operating hours. This violates § 601.21(e).',
      unknown: 'Review supervision requirements against § 601.21(e).',
    },
    stateNotes: [
      'Skilled nursing and therapeutic services must be under physician or RN supervision per 28 Pa. Code § 601.21(e)',
      'Supervisor must be available at all times during operating hours',
      'Supervising physician or RN may also be the administrator per § 601.21(d)/(e)',
      'Supervision ensures quality of all clinical services provided',
    ],
  },

  {
    id: 'PA-A6',
    itemId: 'A6',
    stateCode: 'PA',
    title: 'Ownership and Change Reporting',
    description: 'Agency ownership must be disclosed and changes reported per § 601.11 and § 601.13',
    severity: 'medium',
    regulationCode: '§ 601.11',
    regulationName: 'Ownership',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Have all ownership changes been reported to PA DOH?',
        inputType: 'select',
        options: [
          { value: 'no_changes', label: 'No changes have occurred' },
          { value: 'reported', label: 'Yes, all changes were reported' },
          { value: 'not_reported', label: 'Changes occurred but not yet reported' },
        ],
        required: true,
        helpText: 'Per § 601.11, ownership interest of 5% or more must be disclosed',
      },
      {
        id: 'q2',
        question: 'Per § 601.13(c), do you immediately report catastrophic incidents (fire, flood) or events causing service interruption to PA DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, have procedure in place' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'not_reported', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['no_changes', 'reported'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Ownership and incident reporting meets § 601.11 and § 601.13 requirements.',
      atRisk: 'Incident reporting procedures may need improvement per § 601.13(c).',
      nonCompliant: 'Unreported ownership changes violate § 601.11. Report to PA DOH immediately.',
      unknown: 'Review ownership and reporting requirements.',
    },
    stateNotes: [
      'Ownership interest of 5% or more must be disclosed per 28 Pa. Code § 601.11',
      'Report catastrophic incidents immediately per § 601.13(c)',
      'Changes affecting services must be reported promptly',
      'Owner responsibility requirements per 55 Pa. Code Chapter 52',
    ],
  },

  {
    id: 'PA-A7',
    itemId: 'A7',
    stateCode: 'PA',
    title: 'Coordination of Patient Services',
    description: 'Personnel must maintain liaison to assure effective coordination per § 601.21(h)',
    severity: 'medium',
    regulationCode: '§ 601.21(h)',
    regulationName: 'Coordination',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Do personnel providing services maintain liaison to ensure efforts complement one another per § 601.21(h)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is a written summary report sent to the attending physician at least every 60 days per § 601.21(h)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 60 days or more frequently' },
          { value: 'sometimes', label: 'Sometimes but not consistently' },
          { value: 'no', label: 'No / Less frequently' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do clinical records or case conference minutes establish that effective interchange and coordinated patient evaluation occur?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient service coordination meets § 601.21(h) requirements (60-day physician summary).',
      atRisk: 'Physician summary reports may not be sent every 60 days as required.',
      nonCompliant: 'Written summary reports to physician not sent every 60 days. This violates § 601.21(h).',
      unknown: 'Review coordination procedures against § 601.21(h).',
    },
    stateNotes: [
      'Personnel must maintain liaison to coordinate patient services per 28 Pa. Code § 601.21(h)',
      'Written summary report to attending physician required at least every 60 days',
      'Clinical records or case conference minutes must document coordination',
      'Ensures effective interchange and coordinated patient evaluation',
    ],
  },

  {
    id: 'PA-A8',
    itemId: 'A8',
    stateCode: 'PA',
    title: 'Institutional Planning',
    description: 'Agency must prepare overall plan and budget per § 601.21(i)',
    severity: 'medium',
    regulationCode: '§ 601.21(i)',
    regulationName: 'Planning',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an overall plan and budget prepared under governing body direction per § 601.21(i)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Was the plan developed in conjunction with administrative and health professional staff representatives?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Institutional planning meets § 601.21(i) requirements.',
      atRisk: 'Plan may not include all required staff representatives.',
      nonCompliant: 'No overall plan and budget in place. This violates § 601.21(i).',
      unknown: 'Review institutional planning requirements against § 601.21(i).',
    },
    stateNotes: [
      'Overall plan and budget required under governing body direction per 28 Pa. Code § 601.21(i)',
      'Must be developed with administrative and health professional staff input',
      'Plan guides agency operations and resource allocation',
      'Budget should support adequate staffing and service delivery',
    ],
  },

  // ============================================
  // SECTION B: AGENCY EVALUATION AND REVIEW
  // ============================================
  {
    id: 'PA-B1',
    itemId: 'B1',
    stateCode: 'PA',
    title: 'Annual Policy Review',
    description: 'Professional personnel must establish and annually review policies per § 601.22(a)',
    severity: 'high',
    regulationCode: '§ 601.22(a)',
    regulationName: 'Policy Review',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Do professional personnel (including at least 1 physician and 1 RN) annually review agency policies per § 601.22(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which policy areas are established and annually reviewed? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Scope of services offered' },
          { value: 'admission', label: 'Admission and discharge policies' },
          { value: 'medical_supervision', label: 'Medical supervision and plans of treatment' },
          { value: 'emergency', label: 'Emergency care' },
          { value: 'clinical_records', label: 'Clinical records' },
          { value: 'personnel', label: 'Personnel qualifications' },
          { value: 'program_eval', label: 'Program evaluation' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Annual policy review meets § 601.22(a) requirements.',
      atRisk: 'Policy review may not cover all required areas per § 601.22(a).',
      nonCompliant: 'No annual policy review by professional personnel. This violates § 601.22(a).',
      unknown: 'Review policy review requirements against § 601.22(a).',
    },
    stateNotes: [
      'Professional personnel must include at least 1 physician and 1 RN',
      'Must have appropriate representation from other professional disciplines',
    ],
  },

  {
    id: 'PA-B2',
    itemId: 'B2',
    stateCode: 'PA',
    title: 'Advisory and Evaluation Function',
    description: 'Professional personnel group must meet at least annually per § 601.22(b)',
    severity: 'medium',
    regulationCode: '§ 601.22(b)',
    regulationName: 'Advisory',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does the group of professional personnel meet at least annually to advise the agency on professional issues per § 601.22(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are these meetings documented by dated minutes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Does the group assist in maintaining liaison with other health care providers and community information programs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      // Not maintaining liaison with other health care providers per § 601.22(b)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Advisory and evaluation function meets § 601.22(b) requirements.',
      atRisk: 'Meeting minutes may not be properly documented or group not maintaining liaison with other providers.',
      nonCompliant: 'Professional personnel group does not meet annually. This violates § 601.22(b).',
      unknown: 'Review advisory function requirements against § 601.22(b).',
    },
    stateNotes: [
      'Professional personnel group must meet at least annually per 28 Pa. Code § 601.22(b)',
      'Meetings must be documented by dated minutes',
      'Group advises agency on professional issues affecting patient care',
      'Must maintain liaison with other health care providers and community programs',
    ],
  },

  {
    id: 'PA-B3',
    itemId: 'B3',
    stateCode: 'PA',
    title: 'Annual Program Evaluation',
    description: 'Overall evaluation of agency program required at least annually per § 601.22(c)',
    severity: 'high',
    regulationCode: '§ 601.22(c)',
    regulationName: 'Program Evaluation',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written policies requiring an overall evaluation of the total program at least annually per § 601.22(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the evaluation include both? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy_review', label: 'Overall policy and administrative review' },
          { value: 'clinical_review', label: 'Clinical record review' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Does the evaluation assess if the program is appropriate, adequate, effective, and efficient?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q4',
        question: 'Are results reported to and acted upon by those responsible for agency operation, and maintained as administrative records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countLessThan', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Annual program evaluation meets § 601.22(c) requirements.',
      atRisk: 'Annual program evaluation may be incomplete per § 601.22(c).',
      nonCompliant: 'No annual program evaluation. This violates § 601.22(c).',
      unknown: 'Review program evaluation requirements against § 601.22(c).',
    },
    stateNotes: [
      'Written policies requiring annual program evaluation per 28 Pa. Code § 601.22(c)',
      'Must include policy/administrative review and clinical record review',
      'Evaluation assesses if program is appropriate, adequate, effective, and efficient',
      'Results must be reported to those responsible for agency operation',
      'Maintain evaluation results as administrative records',
    ],
  },

  {
    id: 'PA-B4',
    itemId: 'B4',
    stateCode: 'PA',
    title: 'Quarterly Clinical Record Review',
    description: 'Clinical records must be reviewed at least quarterly per § 601.22(d)',
    severity: 'high',
    regulationCode: '§ 601.22(d)',
    regulationName: 'Record Review',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Do appropriate health professionals review a sample of active and closed clinical records at least quarterly per § 601.22(d)?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Yes, at least quarterly' },
          { value: 'less_frequent', label: 'Less than quarterly' },
          { value: 'no', label: 'No formal review process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is there a continuing review of clinical records for each 60-day period a patient receives services per § 601.22(d)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 60 days' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does the 60-day review determine adequacy of plan of treatment and appropriateness of continuation of care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'quarterly' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'less_frequent' },
        { condition: 'equals', questionId: 'q2', value: 'sometimes' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical record review meets § 601.22(d) requirements (quarterly sample + 60-day continuing review).',
      atRisk: 'Clinical record review frequency may not meet requirements.',
      nonCompliant: 'No quarterly clinical record review or 60-day continuing review. This violates § 601.22(d).',
      unknown: 'Review clinical record review requirements against § 601.22(d).',
    },
    stateNotes: [
      'Sample of active and closed records reviewed quarterly per 28 Pa. Code § 601.22(d)',
      'Continuing review every 60 days for patients receiving services',
      'Review determines adequacy of plan of treatment',
      'Assesses appropriateness of continuation of care',
      'Health professionals conduct the reviews',
    ],
  },

  // ============================================
  // SECTION C: PATIENT CARE
  // ============================================
  {
    id: 'PA-C1',
    itemId: 'C1',
    stateCode: 'PA',
    title: 'Patient Acceptance Criteria',
    description: 'Patient acceptance requirements per § 601.31(a)',
    severity: 'high',
    regulationCode: '§ 601.31(a)',
    regulationName: 'Acceptance',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Are patients accepted based on reasonable expectation that medical, nursing, and social needs can be met per § 601.31(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does patient care follow a written plan of treatment established and periodically reviewed by the attending physician?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does patient care continue under supervision of the attending physician?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient acceptance meets § 601.31(a) requirements.',
      atRisk: 'Patient acceptance criteria or physician supervision may need improvement.',
      nonCompliant: 'No written plan of treatment for patients. This violates § 601.31(a).',
      unknown: 'Review patient acceptance requirements against § 601.31(a).',
    },
    stateNotes: [
      'Accept patients based on reasonable expectation needs can be met per 28 Pa. Code § 601.31(a)',
      'Written plan of treatment required from attending physician',
      'Care continues under physician supervision',
      'Plan must be periodically reviewed by physician',
    ],
  },

  {
    id: 'PA-C2',
    itemId: 'C2',
    stateCode: 'PA',
    title: 'Plan of Treatment',
    description: 'Plan of treatment content requirements per § 601.31(b)',
    severity: 'high',
    regulationCode: '§ 601.31(b)',
    regulationName: 'Plan of Treatment',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Is the plan of treatment developed in consultation with agency staff per § 601.31(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements does the plan of treatment include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'Pertinent diagnoses including mental status' },
          { value: 'services', label: 'Types of services and equipment required' },
          { value: 'frequency', label: 'Frequency of visits' },
          { value: 'prognosis', label: 'Prognosis' },
          { value: 'rehab', label: 'Rehabilitation potential' },
          { value: 'limitations', label: 'Functional limitations' },
          { value: 'activities', label: 'Activities permitted' },
          { value: 'nutrition', label: 'Nutritional requirements' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'safety', label: 'Safety measures to protect against injury' },
          { value: 'instructions', label: 'Instructions for timely discharge or referral' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of treatment contain all required elements per § 601.31(b).',
      atRisk: 'Plans of treatment may be missing some required elements.',
      nonCompliant: 'Plans of treatment do not meet § 601.31(b) requirements.',
      unknown: 'Review plan of treatment requirements against § 601.31(b).',
    },
    stateNotes: [
      'Plan developed in consultation with agency staff per 28 Pa. Code § 601.31(b)',
      'Must include diagnoses, services, frequency, prognosis, limitations',
      'Safety measures to protect against injury required',
      'Instructions for timely discharge or referral must be included',
      'Nutritional requirements and medications documented',
    ],
  },

  {
    id: 'PA-C3',
    itemId: 'C3',
    stateCode: 'PA',
    title: 'Verbal Orders',
    description: 'Verbal orders must be followed by written orders per § 601.31(c)',
    severity: 'medium',
    regulationCode: '§ 601.31(c)',
    regulationName: 'Verbal Orders',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'When care begins based on verbal orders, are written orders signed by the physician and placed in the clinical record promptly?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always promptly' },
          { value: 'usually', label: 'Usually but not always promptly' },
          { value: 'no', label: 'No / Not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Verbal order procedures meet § 601.31(c) requirements.',
      atRisk: 'Written orders may not be obtained promptly after verbal orders.',
      nonCompliant: 'Verbal orders not being promptly followed by written orders. This violates § 601.31(c).',
      unknown: 'Review verbal order procedures against § 601.31(c).',
    },
    stateNotes: [
      'Verbal orders must be promptly followed by written orders per 28 Pa. Code § 601.31(c)',
      'Written orders must be signed by physician',
      'Orders placed in clinical record promptly',
      'Document date and time of verbal order and who received it',
    ],
  },

  // ============================================
  // SECTION D: SKILLED NURSING SERVICE
  // ============================================
  {
    id: 'PA-D1',
    itemId: 'D1',
    stateCode: 'PA',
    title: 'Skilled Nursing Service Requirements',
    description: 'Skilled nursing service requirements per § 601.32',
    severity: 'high',
    regulationCode: '§ 601.32',
    regulationName: 'Skilled Nursing',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Are skilled nursing services provided by or under supervision of a registered nurse per § 601.32(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which services are RNs responsible for? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'initial_eval', label: 'Initial evaluation visit' },
          { value: 'reassess', label: 'Regular re-evaluation of patient nursing needs' },
          { value: 'initiate_plan', label: 'Initiation of the plan of treatment and revisions' },
          { value: 'furnish_services', label: 'Furnishing services requiring RN skill' },
          { value: 'supervise', label: 'Supervision of LPNs and home health aides' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Skilled nursing service meets § 601.32 requirements.',
      atRisk: 'Skilled nursing service may not include all required RN responsibilities.',
      nonCompliant: 'Skilled nursing not provided by or under RN supervision. This violates § 601.32.',
      unknown: 'Review skilled nursing requirements against § 601.32.',
    },
    stateNotes: [
      'Skilled nursing must be provided by or under RN supervision per 28 Pa. Code § 601.32(a)',
      'RN responsible for initial evaluation visit per § 601.32(b)(1)',
      'RN re-evaluates patient nursing needs regularly',
      'RN supervises LPNs and home health aides',
      'RN furnishes services requiring substantial specialized nursing skill',
    ],
  },

  // ============================================
  // SECTION E: HOME HEALTH AIDE SERVICES
  // ============================================
  {
    id: 'PA-E1',
    itemId: 'E1',
    stateCode: 'PA',
    title: 'Home Health Aide Selection and Training',
    description: 'Home health aide requirements per § 601.35(a)',
    severity: 'high',
    regulationCode: '§ 601.35(a)',
    regulationName: 'HHA Selection',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Are home health aides selected based on required factors per § 601.35(a)?',
        inputType: 'checklist',
        options: [
          { value: 'sympathetic', label: 'Sympathetic attitude toward care of the sick' },
          { value: 'directions', label: 'Ability to carry out directions' },
          { value: 'maturity', label: 'Maturity' },
          { value: 'demands', label: 'Ability to deal effectively with demands of the job' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are home health aides trained in required areas? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'self_reliance', label: 'Assisting patients to achieve maximum self-reliance' },
          { value: 'nutrition', label: 'Principles of nutrition and meal preparation' },
          { value: 'aging', label: 'The aging process and emotional problems of illness' },
          { value: 'environment', label: 'Maintaining clean, healthful, pleasant environment' },
          { value: 'reporting', label: 'Changes in patient condition to report' },
          { value: 'agency', label: 'Work of the agency and health team' },
          { value: 'ethics', label: 'Ethics, confidentiality and recordkeeping' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 2 },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide selection and training meets § 601.35(a) requirements.',
      atRisk: 'Home health aide selection or training may be incomplete.',
      nonCompliant: 'Home health aide training does not meet § 601.35(a) requirements.',
      unknown: 'Review home health aide requirements against § 601.35(a).',
    },
    stateNotes: [
      'HHAs selected based on sympathetic attitude and ability per 28 Pa. Code § 601.35(a)',
      'Training in self-reliance, nutrition, aging process required',
      'Must understand changes to report to supervisor',
      'Training in ethics, confidentiality, and recordkeeping required',
      'Document all training in personnel file',
    ],
  },

  {
    id: 'PA-E2',
    itemId: 'E2',
    stateCode: 'PA',
    title: 'Home Health Aide Assignment and Supervision',
    description: 'Home health aide assignment and supervision requirements per § 601.35(b)',
    severity: 'high',
    regulationCode: '§ 601.35(b)',
    regulationName: 'HHA Supervision',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Are home health aides assigned to particular patients by a registered nurse per § 601.35(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are home health aides closely supervised to assure competence in providing care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Are written instructions for patient care prepared by the RN and included in the clinical record?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Home health aide assignment and supervision meets § 601.35(b) requirements.',
      atRisk: 'Home health aide supervision or documentation may need improvement.',
      nonCompliant: 'Home health aides not assigned by RN. This violates § 601.35(b).',
      unknown: 'Review home health aide assignment requirements against § 601.35(b).',
    },
    stateNotes: [
      'HHAs must be assigned to patients by RN per 28 Pa. Code § 601.35(b)',
      'Close supervision required to assure competence',
      'Written patient care instructions prepared by RN',
      'Instructions included in clinical record',
      'Document supervisory visits and competency assessments',
    ],
  },

  // ============================================
  // SECTION F: CLINICAL RECORDS
  // ============================================
  {
    id: 'PA-F1',
    itemId: 'F1',
    stateCode: 'PA',
    title: 'Clinical Record Content',
    description: 'Clinical record content requirements per § 601.36(a)',
    severity: 'high',
    regulationCode: '§ 601.36(a)',
    regulationName: 'Clinical Records',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Is a clinical record maintained for every patient receiving home health care services per § 601.36(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements do clinical records contain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'plan', label: 'Plan of treatment' },
          { value: 'identifying', label: 'Appropriate identifying information' },
          { value: 'physician', label: 'Name of physician' },
          { value: 'drug_dietary', label: 'Drug and dietary treatment' },
          { value: 'activity', label: 'Activity orders' },
          { value: 'clinical_notes', label: 'Signed and dated clinical and progress notes (written day of service, in record weekly)' },
          { value: 'summary', label: 'Copies of summary reports sent to physician' },
          { value: 'discharge', label: 'Discharge summary' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per § 601.36(a).',
      atRisk: 'Clinical records may be missing some required elements.',
      nonCompliant: 'Clinical records do not meet § 601.36(a) requirements.',
      unknown: 'Review clinical record requirements against § 601.36(a).',
    },
    stateNotes: [
      'Clinical notes must be written the day service is rendered',
      'Notes must be incorporated into clinical record no less often than weekly',
    ],
  },

  {
    id: 'PA-F2',
    itemId: 'F2',
    stateCode: 'PA',
    title: 'Clinical Record Retention (7 Years)',
    description: 'Clinical records must be retained 7 years after discharge per § 601.36(b)',
    severity: 'high',
    regulationCode: '§ 601.36(b)',
    regulationName: 'Record Retention',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records after patient discharge?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets PA requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Pennsylvania § 601.36(b) requires 7 years retention after discharge',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical record retention meets 7-year requirement per § 601.36(b).',
      atRisk: 'Record retention may not meet 7-year requirement.',
      nonCompliant: 'Record retention does not meet 7-year requirement per § 601.36(b).',
      unknown: 'Review record retention policy against § 601.36(b).',
    },
    stateNotes: [
      'Pennsylvania requires 7-year retention after discharge per 28 Pa. Code § 601.36(b)',
      'This exceeds Medicare 5-year requirement',
      'Maintain records in secure, accessible location',
      'If closing, arrange for continued record storage and notify PA DOH',
    ],
  },

  // ============================================
  // SECTION G: BACKGROUND SCREENING
  // ============================================
  {
    id: 'PA-G1',
    itemId: 'G1',
    stateCode: 'PA',
    title: 'Criminal Background Checks',
    description: 'Background check requirements under Older Adults Protective Services Act',
    severity: 'high',
    regulationCode: '§ 10225.502',
    regulationName: 'Background Check',
    agencyName: 'Pennsylvania State Police',
    agencyAbbr: 'PSP',
    agencyPhone: '1-888-783-7972',
    agencyUrl: 'https://epatch.state.pa.us',
    questions: [
      {
        id: 'q1',
        question: 'Do you obtain PA State Police criminal background checks for all direct care staff before patient contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff before patient contact' },
          { value: 'most', label: 'Most staff, some pending' },
          { value: 'no', label: 'No / Not consistently' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For staff who have NOT been PA residents for 2+ years, do you also obtain FBI fingerprint background checks?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all applicable staff' },
          { value: 'some', label: 'Some staff' },
          { value: 'no', label: 'No / Not tracking residency' },
          { value: 'na', label: 'N/A - All staff are 2+ year PA residents' },
        ],
      },
      {
        id: 'q3',
        question: 'Do employees begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per 35 P.S. § 10225.502: Employees must not have patient contact until background check clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if not obtaining checks
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT only if all checks complete AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['all', 'na'] },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet Pennsylvania requirements.',
      atRisk: 'Some background checks may be incomplete or pending.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per 35 P.S. § 10225.502, employees must not have patient contact until background check clearance is received. Immediately remove uncleared staff from patient care.',
      unknown: 'Verify background check procedures comply with Pennsylvania law.',
    },
    stateNotes: [
      'PA State Police check required for all direct care staff',
      'FBI fingerprint check required if not PA resident for 2+ years',
      'Prohibited offenses listed in 6 Pa. Code § 15.143',
      'Check via PATCH: https://epatch.state.pa.us',
    ],
  },

  {
    id: 'PA-G2',
    itemId: 'G2',
    stateCode: 'PA',
    title: 'Child Abuse Clearance (If Serving Minors)',
    description: 'ChildLine clearance required if serving patients under 18',
    severity: 'high',
    regulationCode: 'Chapter 63',
    regulationName: 'Child Abuse',
    agencyName: 'Pennsylvania Department of Human Services',
    agencyAbbr: 'DHS',
    agencyPhone: '(717) 783-6211',
    agencyUrl: 'https://www.compass.state.pa.us/CWIS/public/home',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency serve patients under age 18?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff who may have contact with minors have ChildLine clearances?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all applicable staff' },
          { value: 'most', label: 'Most staff' },
          { value: 'no', label: 'No / Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'ChildLine clearances are complete for all applicable staff.',
      atRisk: 'Some staff serving minors may lack ChildLine clearances.',
      nonCompliant: 'Staff interacting with minors without ChildLine clearances violates 23 Pa.C.S. Chapter 63.',
      unknown: 'If serving minors, verify all staff have ChildLine clearances.',
    },
    stateNotes: [
      'ChildLine clearance required if serving patients under 18 per 23 Pa.C.S. Chapter 63',
      'Obtain via https://www.compass.state.pa.us/CWIS/public/home',
      'Clearance must be received before patient contact with minors',
      'Renew every 60 months per Child Protective Services Law',
    ],
  },

  {
    id: 'PA-G3',
    itemId: 'G3',
    stateCode: 'PA',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list verification requirements',
    severity: 'high',
    regulationCode: '§ 1320a-7',
    regulationName: 'Exclusion List',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you participate in Medicare or Medicaid?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you verify employees against OIG exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'hire_annual', label: 'At hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Do not check' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'hire_annual'], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['hire_only', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification meets federal requirements.',
      atRisk: 'OIG checks should be monthly per OIG guidance.',
      nonCompliant: 'Not checking OIG exclusion lists. Billing for excluded employees is fraud with CMPs up to $100,000.',
      unknown: 'Implement OIG exclusion list verification at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG exclusion list check required for Medicare/Medicaid participation per 42 U.S.C. § 1320a-7',
      'Monthly verification recommended per OIG guidance',
      'Check at exclusions.oig.hhs.gov',
      'Also check SAM.gov exclusion database',
      'Civil monetary penalties up to $100,000 for employing excluded individuals',
    ],
  },

  // ============================================
  // SECTION H: COMPLIANCE STANDARDS
  // ============================================
  {
    id: 'PA-H1',
    itemId: 'H1',
    stateCode: 'PA',
    title: 'Compliance with Federal and State Standards',
    description: 'Agency must comply with all applicable standards per § 601.3(b)',
    severity: 'high',
    regulationCode: '§ 601.3(b)',
    regulationName: 'Compliance',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pa.gov/agencies/health/facilities/out-patient-healthcare-facilities/home-health.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain compliance with applicable professional licensure standards? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'medicine', label: 'State Board of Medicine (49 Pa. Code Chapters 16-18)' },
          { value: 'nursing', label: 'State Board of Nursing (49 Pa. Code Chapter 21)' },
          { value: 'pharmacy', label: 'State Board of Pharmacy (49 Pa. Code Chapter 27)' },
          { value: 'pt', label: 'State Board of Physical Therapy (49 Pa. Code Chapter 40)' },
          { value: 'ot', label: 'Occupational Therapy Practice Act (63 P.S. §§ 1501-1519)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you comply with communicable disease requirements (28 Pa. Code Chapter 27)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you comply with controlled substances requirements (28 Pa. Code Chapter 25)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / N/A' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // Not complying with controlled substances requirements per 28 Pa. Code Chapter 25
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Agency meets applicable professional and regulatory standards per § 601.3(b).',
      atRisk: 'Some compliance areas may need review, including controlled substances compliance per 28 Pa. Code Chapter 25.',
      nonCompliant: 'Agency may not be meeting all applicable standards per § 601.3(b).',
      unknown: 'Review compliance with all applicable standards.',
    },
    stateNotes: [
      'Must comply with State Board of Nursing (49 Pa. Code Chapter 21)',
      'Physical therapy compliance per 49 Pa. Code Chapter 40',
      'Communicable disease requirements per 28 Pa. Code Chapter 27',
      'Controlled substances per 28 Pa. Code Chapter 25',
      'All professional licenses must be current and verified',
    ],
  },

  {
    id: 'PA-H2',
    itemId: 'H2',
    stateCode: 'PA',
    title: 'Current Copy of Regulations Maintained',
    description: 'Agency must maintain current copy of subpart per § 601.3(a)',
    severity: 'low',
    regulationCode: '§ 601.3(a)',
    regulationName: 'Regulations',
    agencyName: 'Pennsylvania Department of Health',
    agencyAbbr: 'PA DOH',
    agencyPhone: '(717) 787-8015',
    agencyUrl: 'https://www.pacodeandbulletin.gov/Display/pacode?file=/secure/pacode/data/028/chapter601/chap601toc.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain a current copy of 28 Pa. Code Chapter 601 at the agency per § 601.3(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Current regulations maintained per § 601.3(a).',
      atRisk: 'Maintain current copy of 28 Pa. Code Chapter 601 at agency.',
      nonCompliant: 'Current regulations not maintained. This is required per § 601.3(a).',
      unknown: 'Verify current regulations are maintained at agency.',
    },
    stateNotes: [
      'Current copy of 28 Pa. Code Chapter 601 required at agency per § 601.3(a)',
      'Access online at pacodeandbulletin.gov',
      'Review for regulatory updates periodically',
      'Staff should be familiar with applicable regulations',
    ],
  },
];

export default PENNSYLVANIA_QUESTIONS;